import React, { useState, useMemo } from 'react';
import { SeoReport, SeoReportKeyword } from 'store/services/admin/adminSeoApi';

type SortField = 'query' | 'position' | 'impressions' | 'clicks' | 'ctr';
type SortDirection = 'asc' | 'desc';

const getPositionClass = (position: number): string => {
  if (position <= 10) return 'position-green';
  if (position <= 15) return 'position-yellow';
  return 'position-orange';
};

interface Props {
  data: SeoReport;
}

const GapZoneTab: React.FC<Props> = ({ data }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('impressions');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'query' ? 'asc' : 'desc');
    }
  };

  const filteredAndSorted = useMemo(() => {
    let keywords = [...data.gapZoneKeywords];

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      keywords = keywords.filter(k => k.query.toLowerCase().includes(lower));
    }

    keywords.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'query') {
        comparison = a.query.localeCompare(b.query);
      } else {
        comparison = a[sortField] - b[sortField];
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return keywords;
  }, [data, searchTerm, sortField, sortDirection]);

  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <>
      {/* Summary Cards */}
      <div className="seo-stats">
        <div className="seo-stat-card">
          <span className="seo-stat-label">Total Impressions</span>
          <span className="seo-stat-value">{data.summary.totalImpressions.toLocaleString()}</span>
        </div>
        <div className="seo-stat-card">
          <span className="seo-stat-label">Total Clicks</span>
          <span className="seo-stat-value">{data.summary.totalClicks.toLocaleString()}</span>
        </div>
        <div className="seo-stat-card">
          <span className="seo-stat-label">Avg Position</span>
          <span className="seo-stat-value">{data.summary.avgPosition.toFixed(1)}</span>
        </div>
        <div className="seo-stat-card">
          <span className="seo-stat-label">Gap Zone Keywords</span>
          <span className="seo-stat-value">{data.summary.gapZoneCount}</span>
        </div>
      </div>

      {/* Search */}
      <div className="seo-controls">
        <input
          type="text"
          placeholder="Search keywords..."
          className="seo-search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <span className="seo-result-count">
          {filteredAndSorted.length} keyword{filteredAndSorted.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="seo-table-card">
        <div className="seo-table-header">
          <div className="seo-col-keyword seo-sortable" onClick={() => handleSort('query')}>
            Keyword{getSortIndicator('query')}
          </div>
          <div className="seo-col-position seo-sortable" onClick={() => handleSort('position')}>
            Position{getSortIndicator('position')}
          </div>
          <div className="seo-col-impressions seo-sortable" onClick={() => handleSort('impressions')}>
            Impressions{getSortIndicator('impressions')}
          </div>
          <div className="seo-col-clicks seo-sortable" onClick={() => handleSort('clicks')}>
            Clicks{getSortIndicator('clicks')}
          </div>
          <div className="seo-col-ctr seo-sortable" onClick={() => handleSort('ctr')}>
            CTR{getSortIndicator('ctr')}
          </div>
        </div>

        {filteredAndSorted.length === 0 ? (
          <div className="seo-no-results">
            {searchTerm ? 'No keywords match your search.' : 'No gap zone keywords found.'}
          </div>
        ) : (
          filteredAndSorted.map((keyword: SeoReportKeyword) => (
            <div className="seo-table-row" key={keyword.query}>
              <div className="seo-col-keyword">{keyword.query}</div>
              <div className="seo-col-position">
                <span className={`seo-position-badge ${getPositionClass(keyword.position)}`}>
                  {keyword.position.toFixed(1)}
                </span>
              </div>
              <div className="seo-col-impressions">{keyword.impressions.toLocaleString()}</div>
              <div className="seo-col-clicks">{keyword.clicks.toLocaleString()}</div>
              <div className="seo-col-ctr">{(keyword.ctr * 100).toFixed(1)}%</div>
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default GapZoneTab;
